export const POLICY_UPDATED = 'November 2, 2026';

export const REFUND_POLICY = {
  title: 'No-refund policy',
  label: 'All sales are final',
  summary:
    "All sales are final. Our plans and Desktop Telly licences are digital products, so we don't offer refunds once an order is paid. If something isn't working, we'll work through it with you through technical support until it's sorted.",
  sections: [
    {
      h: 'All sales are final',
      p: [
        "Ghost Cable subscriptions and Desktop Telly licences are digital products that are delivered as soon as your line is activated or your licence key is issued. Because they can't be returned, we don't offer refunds, partial refunds or credits once payment has been made.",
      ],
    },
    {
      h: 'Technical problems',
      p: [
        "If something isn't working, open a support ticket from your account. We'll work through it with you: setup help, device and app troubleshooting, switching servers, or moving a Desktop Telly licence to a new device. We stay with it until the problem is resolved.",
      ],
    },
    {
      h: 'Try before you pay',
      p: [
        'Not sure it will work on your device? Ask for a free trial when you order a plan. We set up the trial before sending an invoice, so you can test everything first.',
      ],
    },
    {
      h: 'Before you pay',
      p: [
        "Nothing is charged on this website. If you change your mind before paying your invoice, just let us know and we'll cancel the order.",
      ],
    },
    {
      h: 'Renewals',
      p: [
        'Nothing renews automatically. Each new term is a separate invoice you choose to pay, and the same policy applies to it.',
      ],
    },
  ],
  accept:
    'I understand these are digital products and all sales are final. Technical problems are handled through support.',
};

export const TERMS = {
  title: 'Terms of Service',
  label: 'Network directives',
  summary:
    'By using the Ghost Cable site, placing an order, or paying an invoice, you agree to these Network Directives. Please read them carefully. They keep the grid fast, stable and fair for everyone connected to it.',
  sections: [
    {
      h: '1. Digital provisioning',
      p: [
        'Ghost Cable is a digital service. We provide access credentials (an Xtream Codes login and a TV guide link) to original Ghost Cable channels and programming, plus Desktop Telly player licences.',
        "Hardware independence: We don't sell, rent or support physical hardware. You are responsible for compatible hardware (for example a Firestick, Nvidia Shield or Android box) and a player app (for example TiviMate or Televizo).",
        'Manual activation: Every order is reviewed, invoiced and activated by hand. Most orders are activated quickly, but please allow normal processing time during busy hours.',
      ],
    },
    {
      h: '2. The no-refund directive',
      p: [
        'Plans and Desktop Telly licences are digital products, delivered as soon as your line is activated or your key is issued. All sales are final.',
        "We don't issue refunds, partial credits or payment reversals for activated orders. If something isn't working, we'll work through it with you through technical support until it's resolved.",
        "If you're not sure your device or connection will cope, ask for a free trial before you commit to a longer plan. The full policy is on the No-refund policy page.",
      ],
    },
    {
      h: '3. Credential security and connection limits',
      p: [
        'Your credentials are issued to you alone.',
        "Credential protection: Don't share your login, post it publicly, or resell access.",
        'Connection limits: Your line allows the number of simultaneous connections you bought at checkout. Connecting more devices than that may be blocked automatically.',
        'Desktop Telly: Each licence is active on one device at a time. You can move it to a new device through support.',
        'Breaking these rules may get your line closed permanently, without a refund.',
      ],
    },
    {
      h: '4. Network stability and your ISP',
      p: [
        "We keep our core network running reliably, but we don't control your home connection.",
        "ISP throttling: Ghost Cable isn't responsible for buffering, dropped frames or lag caused by your internet provider.",
        'VPNs: The service works with VPNs. If your provider interferes with streaming, a VPN can help.',
        'Maintenance: We schedule maintenance from time to time to keep the guide (EPG) and line-up up to date.',
      ],
    },
    {
      h: '5. Content availability',
      p: [
        'The Ghost Cable line-up is updated regularly. Channels, schedules and on-demand titles may change, move or be removed without notice.',
      ],
    },
    {
      h: '6. Payments and renewals',
      p: [
        'No payment is taken on this website. After you send an order request, we send an invoice for the method you chose. Prices are billed in USD; amounts in other currencies are estimates.',
        'Nothing renews automatically. Each new term is a separate invoice that you choose to pay.',
      ],
    },
    {
      h: '7. Changes to these directives',
      p: [
        'We may update these Network Directives by posting changes on this page. Please check back from time to time. If you keep using the service after an update, you accept the updated terms.',
      ],
    },
  ],
  signoff: 'Ghost Cable. Unseen. Unthrottled. Unstoppable.',
};

export const PRIVACY = {
  title: 'Privacy Policy',
  label: 'The anonymity protocol',
  summary:
    'At Ghost Cable, our main goal is to keep you off the radar. We offer fast access without invasive tracking or data harvesting. This policy explains what we collect, why we need it, and how we protect it.',
  sections: [
    {
      h: '1. What we collect',
      p: [
        'We only collect what we need to set up your line and support it:',
        'Contact details: your name, your email address, and the Discord, WhatsApp or Telegram handle you give us. We use these to send invoices, credentials, setup guides and support replies.',
        'Order details: your plan, term, number of connections, device, preferred payment method, discount code, any notes you add, and the time you accepted the no-refund policy.',
        'Network data: our automated firewall briefly monitors IP addresses. We use this only to enforce your connection limit and to stop credential sharing, unauthorised access and attacks.',
      ],
    },
    {
      h: '2. How we use it',
      p: [
        'We use your data only to keep your line working. That means we use it to:',
        'Prepare and activate your order by hand, and issue Desktop Telly licence keys.',
        'Send invoices, record payments, set up trials, and send renewal and review reminders.',
        'Answer support tickets and help with setup.',
        'Find and close unauthorised connections that are using your credentials.',
      ],
    },
    {
      h: '3. Sharing and logging',
      p: [
        "We don't sell, trade or leak your data to ad agencies, telecom companies or data brokers.",
        "No viewing logs: We don't track, log or sell what you watch.",
        'Payments: You pay by invoice through the method you chose (card processor, Wise, crypto or e-Transfer). Ghost Cable never stores card numbers or banking details.',
      ],
    },
    {
      h: '4. Security',
      p: [
        'The site uses industry-standard encryption (SSL/TLS) to protect your details in transit. Your orders, credentials, receipts and tickets sit behind your own account, protected by your email address and password, and no other customer can see them. We also recommend running a VPN on your own devices.',
      ],
    },
    {
      h: '5. Cookies and local storage',
      p: [
        "The site uses very little local data. It keeps you signed in and remembers your light or dark theme on your device. We don't use cross-site tracking pixels or advertising cookies.",
      ],
    },
    {
      h: '6. Your rights',
      p: [
        'You control your data. To have it deleted, open a support ticket or contact us. Once your plan has expired, we can permanently erase your details.',
      ],
    },
    {
      h: '7. Jurisdiction',
      p: [
        "Ghost Cable is run from British Columbia, Canada, and this policy follows the applicable local privacy and data-protection standards. If we change how we handle data in any important way, we'll update this page.",
      ],
    },
  ],
  signoff: 'Stay off the radar. Stay ghostly.',
};
